import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { Chart, ChartDataset, ChartOptions } from 'chart.js/auto'
import moment from 'moment'
import { BaseChartDirective } from 'ng2-charts'
import { Subscription } from 'rxjs'
import { AccordionEmissionService } from 'src/app/services/accordionEmission/accordionEmission.service'

import { buildPointStyles, insertSplitDataPoint, TooltipLine } from '../utils'
import { ChartUtilities } from '../utils/chart-utlities'

@Component({
  selector: 'app-line-bar-chart-sync',
  templateUrl: './line-bar-chart-sync.component.html',
  styleUrls: ['./line-bar-chart-sync.component.scss'],
})
export class LineBarChartSyncComponent implements OnChanges, AfterViewInit {
  @ViewChild('hoverLineCanvas1') hoverLineCanvas1: ElementRef<HTMLCanvasElement>
  @ViewChild('hoverLineCanvas2') hoverLineCanvas2: ElementRef<HTMLCanvasElement>
  @ViewChildren(BaseChartDirective) charts: QueryList<BaseChartDirective>

  @Input() chartHeight: string
  @Input() ariaLabel: string
  @Input() datasets: ChartDataset[]
  @Input() barDatasets: ChartDataset[]
  @Input() labels: string[]
  @Input() xMaxTicksLimit: number
  @Input() xScaleTitle: string
  @Input() shareSplitDate: string | null = null
  @Input() shareSplitPrice: number | null = null
  yScaleTitle = 'Price & NAV'
  color = 'rgba(234, 234, 234, 1)'

  @Output() chartMouseOut = new EventEmitter<any>()
  @Output() activeItemChanged = new EventEmitter<any>()
  @Output() dataUpdated = new EventEmitter<{
    labels: string[]
    datasets: any[]
    barDatasets: any[]
    splitIndex: number
  }>()

  private ticksArray: any[]
  private accordionSubscription: Subscription
  private splitIndex = -1
  public options: ChartOptions
  public myCharts = []
  lineChartData: any
  barChartData: any

  @HostListener('document:mouseout', ['$event'])
  onChartMouseOut(event: Event): void {
    const chart1 = document.getElementById('chart1')
    const chart2 = document.getElementById('chart2')
    if (event.target === chart1 || event.target === chart2) {
      this.handleMouseOut(0, event)
      this.handleMouseOut(1, event)
    }
  }

  private handleMouseOut(index: number, event: Event): void {
    const currentCharts = this.charts.toArray()
    this.chartMouseOut.emit(event)
    this.clearTooltips(currentCharts[index])
    this.clearLines()
  }

  ngAfterViewInit() {
    if (this.charts) this.charts.toArray().forEach((chart) => chart?.update())
    const canvas = this.hoverLineCanvas1.nativeElement
    const canvas2 = this.hoverLineCanvas2.nativeElement

    canvas.width = canvas.parentElement.offsetWidth
    canvas.height = canvas.parentElement.offsetHeight
    canvas2.width = canvas2.parentElement.offsetWidth
    canvas2.height = canvas2.parentElement.offsetHeight
  }

  ngOnChanges() {
    this.applySplitData()

    this.optionsBar.scales.x.grid.color = this.insertLineAt(2, this.color)
    if (this.charts) this.charts.toArray().forEach((chart) => chart?.update())
    if (this.datasets && this.datasets.length > 0) this.configureDatasets()

    this.lineChartData = {
      labels: this.labels,
      datasets: this.datasets,
    }
    this.barChartData = {
      labels: this.labels,
      datasets: this.barDatasets,
    }

    this.dataUpdated.emit({
      labels: this.labels,
      datasets: this.datasets,
      barDatasets: this.barDatasets,
      splitIndex: this.splitIndex,
    })

    this.options = {
      responsive: true,
      datasets: {
        line: {
          pointStyle: 'circle',
          pointHoverRadius: 5,
          pointRadius: 1,
        },
      },
      hover: {
        mode: 'index',
        intersect: false,
      },
      onHover: (event) => this.chartHovered(event, 1),
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          intersect: false,
          mode: 'index',
          callbacks: {
            title: (items) => moment(items[0]?.label).format('MMM/DD/YYYY'),
            label: (item) => {
              const label = item.dataset.label || ''
              const defaultLabel = `${label}: $${Number(
                item.formattedValue,
              ).toFixed(2)}`
              if (this.splitIndex >= 0 && item.dataIndex === this.splitIndex) {
                return item.datasetIndex === 0 ? 'Share split' : ''
              }
              return defaultLabel
            },
          },
        },
      },
      scales: {
        x: {
          type: 'time',
          stacked: true,
          title: {
            display: Boolean(this.xScaleTitle),
            text: this.xScaleTitle,
          },
          grid: {
            display: false,
          },
          beforeUpdate: () => (this.ticksArray = []),
          ticks: {
            maxTicksLimit: 5,
            maxRotation: 0,
            padding: 8,
            callback: (tick, index, ticks) =>
              ChartUtilities.pruneXAxisTicks(
                tick,
                index,
                ticks,
                this.xMaxTicksLimit,
                this.ticksArray,
              ),
          },
        },
        y: {
          title: {
            display: Boolean(this.yScaleTitle),
            text: this.yScaleTitle,
          },
          grid: {
            display: true,
          },
          ticks: {
            maxTicksLimit: 4,
            padding: 20,
            callback: (value) => `$${value}`,
          },
          afterBuildTicks: (chart) => (chart.ticks = chart.ticks.slice(1)),
          afterFit: (scaleInstance) => (scaleInstance.width = 100),
        },
      },
    }
  }

  private applySplitData(): void {
    const allDatasets = [...(this.datasets ?? []), ...(this.barDatasets ?? [])]
    const lineCount = this.datasets?.length ?? 0
    const { labels, datasets, splitIndex } = insertSplitDataPoint(
      this.labels,
      allDatasets,
      this.shareSplitDate,
      this.shareSplitPrice,
    )
    this.splitIndex = splitIndex
    this.datasets = datasets.slice(0, lineCount)
    this.barDatasets = datasets.slice(lineCount)
    this.labels = labels
  }

  private configureDatasets(): void {
    const pointStyles = buildPointStyles(
      this.datasets[0]?.data?.length ?? 0,
      this.splitIndex,
    )
    this.datasets[0] = {
      ...this.datasets[0],
      borderColor: '#1BA39C',
      backgroundColor: 'rgb(75, 192, 192)',
      ...pointStyles,
    } as ChartDataset

    if (this.datasets.length > 1) {
      this.datasets[1] = {
        ...this.datasets[1],
        borderColor: '#717777',
        backgroundColor: '#717777',
        pointBorderColor: '#717777',
        pointBackgroundColor: '#717777',
        pointHoverBackgroundColor: '#717777',
      } as ChartDataset
    }
  }

  ngOnDestroy() {
    if (this.accordionSubscription) {
      this.accordionSubscription.unsubscribe()
    }
  }

  private observeCanvasSizeChanges(): void {
    const canvas1 = this.hoverLineCanvas1.nativeElement
    const canvas2 = this.hoverLineCanvas2.nativeElement

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        const parent1 = canvas1.parentElement
        const parent2 = canvas2.parentElement

        if (
          parent1.offsetWidth > 0 &&
          parent1.offsetHeight > 0 &&
          parent2.offsetWidth > 0 &&
          parent2.offsetHeight > 0
        ) {
          canvas1.width = parent1.offsetWidth
          canvas1.height = parent1.offsetHeight
          canvas2.width = parent2.offsetWidth
          canvas2.height = parent2.offsetHeight

          observer.disconnect()
        }
      })
    })

    observer.observe(canvas1.parentElement, {
      attributes: true,
      childList: true,
      subtree: true,
    })
    observer.observe(canvas2.parentElement, {
      attributes: true,
      childList: true,
      subtree: true,
    })
  }

  constructor(private accordionEmissionService: AccordionEmissionService) {
    Chart.register(TooltipLine)

    this.accordionSubscription =
      this.accordionEmissionService.accordionOpened$.subscribe(() => {
        this.observeCanvasSizeChanges()
      })
  }

  get splitDateInRange(): boolean {
    if (!this.shareSplitDate || !this.labels || this.labels.length === 0)
      return false
    const splitTime = new Date(this.shareSplitDate).getTime()
    const firstDate = new Date(this.labels[0]).getTime()
    const lastDate = new Date(this.labels[this.labels.length - 1]).getTime()
    return splitTime >= firstDate && splitTime <= lastDate
  }

  chartHovered(event, index: number): void {
    const currentCharts = this.charts.toArray()
    const points = currentCharts[0].chart.getElementsAtEventForMode(
      event,
      'nearest',
      { intersect: false },
      true,
    )

    if (points[0]) {
      const dataset = points[0].datasetIndex
      const datapoint = points[0].index

      currentCharts[index].chart.setActiveElements([
        { datasetIndex: dataset, index: datapoint },
      ])
      currentCharts[index].chart.tooltip.setActiveElements(
        [{ datasetIndex: dataset, index: datapoint }],
        { x: points[0].element.x, y: points[0].element.y },
      )
      currentCharts[index].chart.update()

      this.createLine(currentCharts, datapoint)
      this.activeItemChanged.emit({ index: datapoint })
    }
  }

  private clearTooltips(item: BaseChartDirective) {
    item.chart.setActiveElements([])
    item.chart.tooltip.setActiveElements([], { x: 0, y: 0 })
    item.chart.update()
  }

  private createLine(currentCharts, datapoint): void {
    const canvases = [this.hoverLineCanvas1, this.hoverLineCanvas2]
    const padding = 30

    currentCharts.forEach((chart, index) => {
      const canvas = canvases[index]
      const ctx = canvas.nativeElement.getContext('2d')
      if (!ctx) return

      ctx.clearRect(
        0,
        0,
        canvas.nativeElement.width,
        canvas.nativeElement.height,
      )

      const meta = chart.chart.getDatasetMeta(0)
      const dataElement = meta.data[datapoint]

      if (dataElement) {
        const tooltipX = dataElement.x

        ctx.beginPath()
        ctx.moveTo(tooltipX, padding)
        ctx.lineTo(tooltipX, canvas.nativeElement.height - padding)
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)'
        ctx.stroke()
      }
    })
  }

  private clearLines(): void {
    const canvases = [this.hoverLineCanvas1, this.hoverLineCanvas2]

    canvases.forEach((canvas) => {
      const ctx = canvas.nativeElement.getContext('2d')
      if (ctx) {
        ctx.clearRect(
          0,
          0,
          canvas.nativeElement.width,
          canvas.nativeElement.height,
        )
      }
    })
  }

  xScaleTitle1 = ''
  yScaleTitle1 = 'Premium/Discount'

  public optionsBar: ChartOptions = {
    responsive: true,
    borderColor: '#1BA39C',
    datasets: {
      line: {
        hoverBackgroundColor: 'rgb(75, 192, 192)',
        hoverBorderJoinStyle: 'bevel',
        pointStyle: 'circle',
        pointHoverBackgroundColor: 'rgb(75, 192, 192)',
        pointHoverRadius: 5,
        pointRadius: 1,
        pointBorderColor: 'rgb(75, 192, 192)',
        pointBackgroundColor: 'rgb(75, 192, 192)',
      },
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
    onHover: (event) => this.chartHovered(event, 0),
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        intersect: false,
        mode: 'index',
        padding: 8,
        cornerRadius: 6,
        caretPadding: 5,
        callbacks: {
          title: function (tooltipItems) {
            const tooltipItem = tooltipItems[0]
            return moment(tooltipItem.label).format('MMM/DD/YYYY')
          },
          label: function (tooltipItem) {
            return `${Number(tooltipItem.formattedValue).toFixed(2)}%`
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        title: {
          display: Boolean(this.xScaleTitle1),
          text: this.xScaleTitle1,
        },
        grid: {
          display: false,
          lineWidth: 1,
          tickLength: 0,
          color: this.insertLineAt(2, this.color),
        },
        beforeUpdate: () => (this.ticksArray = []),
        ticks: {
          maxTicksLimit: 5,
          maxRotation: 0,
          padding: 8,
          callback: (tick, index, ticks) =>
            ChartUtilities.pruneXAxisTicks(
              tick,
              index,
              ticks,
              this.xMaxTicksLimit,
              this.ticksArray,
            ),
        },
      },
      y: {
        title: {
          display: Boolean(this.yScaleTitle1),
          text: this.yScaleTitle1,
        },
        grid: {
          lineWidth: 1,
          color: this.color,
        },
        ticks: {
          maxTicksLimit: 4,
          padding: 20,
          callback: (value) => `${value} %`,
        },
        afterFit: (scaleInstance) => (scaleInstance.width = 100),
      },
    },
  }

  insertLineAt(index, color) {
    if (this.datasets) {
      return this.mapArray(this.datasets[0].data, (i) =>
        index === i ? color : 'transparent',
      )
    }
    return ''
  }

  mapArray(arr, mapFunc): any[] {
    const mapArr = []
    for (let i = 0; i < arr.length; i++) {
      const result = mapFunc(arr[i], i, arr)
      mapArr.push(result)
    }
    return mapArr
  }
}
